import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Player, PlayerStatistics } from '../types/player';
import { PlayerService } from '../services/playerService';
import { StatisticsService } from '../services/statisticsService';

interface PlayerState {
  // 状态
  players: Player[];
  statistics: PlayerStatistics[];
  isLoading: boolean;
  error: string | null;

  // 操作
  loadPlayers: () => Promise<void>;
  addPlayer: (name: string, number?: number) => Promise<void>;
  removePlayer: (playerId: string) => Promise<void>;
  loadStatistics: () => Promise<void>;
  initializeDefaultPlayers: () => Promise<void>;
  clearError: () => void;

  // 计算属性
  getPlayerById: (id: string) => Player | undefined;
  getPlayerByNumber: (number: number) => Player | undefined;
  getActivePlayers: () => Player[];
  getNextAvailableNumber: () => number;
}

export const usePlayerStore = create<PlayerState>()(
  subscribeWithSelector((set, get) => ({
    // 初始状态
    players: [],
    statistics: [],
    isLoading: false,
    error: null,

    // 加载所有玩家
    loadPlayers: async () => {
      set({ isLoading: true, error: null });

      try {
        const players = await PlayerService.getAllActivePlayers();
        set({
          players,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '加载玩家失败',
          isLoading: false,
        });
      }
    },

    // 添加玩家
    addPlayer: async (name: string, number?: number) => {
      set({ isLoading: true, error: null });

      try {
        // 如果没有指定编号，获取下一个可用编号
        const playerNumber = number ?? await PlayerService.getNextAvailableNumber();
        
        const newPlayer = await PlayerService.addPlayer(name, playerNumber);
        
        set((state) => ({
          players: [...state.players, newPlayer].sort((a, b) => a.number - b.number),
          isLoading: false,
        }));
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '添加玩家失败',
          isLoading: false,
        });
      }
    },

    // 移除玩家
    removePlayer: async (playerId: string) => {
      set({ isLoading: true, error: null });

      try {
        await PlayerService.removePlayer(playerId);
        
        set((state) => ({
          players: state.players.filter(p => p.id !== playerId),
          isLoading: false,
        }));
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '移除玩家失败',
          isLoading: false,
        });
      }
    },

    // 加载统计信息
    loadStatistics: async () => {
      try {
        const statistics = await StatisticsService.getAllPlayerStatistics();
        set({ statistics });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '加载统计信息失败',
        });
      }
    },

    // 初始化默认玩家
    initializeDefaultPlayers: async () => {
      set({ isLoading: true, error: null });

      try {
        await PlayerService.initializeDefaultPlayers();
        
        // 重新加载玩家列表
        const players = await PlayerService.getAllActivePlayers();
        set({
          players,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '初始化默认玩家失败',
          isLoading: false,
        });
      }
    },

    // 清除错误
    clearError: () => {
      set({ error: null });
    },

    // 根据ID获取玩家
    getPlayerById: (id: string) => {
      const { players } = get();
      return players.find(p => p.id === id);
    },

    // 根据编号获取玩家
    getPlayerByNumber: (number: number) => {
      const { players } = get();
      return players.find(p => p.number === number);
    },

    // 获取活跃玩家
    getActivePlayers: () => {
      const { players } = get();
      return players.filter(p => p.isActive);
    },

    // 获取下一个可用编号
    getNextAvailableNumber: () => {
      const { players } = get();
      const usedNumbers = players.map(p => p.number);
      let nextNumber = 1;
      
      while (usedNumbers.includes(nextNumber)) {
        nextNumber++;
      }
      
      return nextNumber;
    },
  }))
);

// 自动加载统计信息当玩家列表变化时
usePlayerStore.subscribe(
  (state) => state.players,
  () => {
    // 延迟加载统计信息，避免频繁请求
    setTimeout(() => {
      usePlayerStore.getState().loadStatistics();
    }, 500);
  }
);